import { useCallback, useEffect, useRef, useState } from 'react';

const FADE_OUT_MS = 300;

interface UseToastAutoDismissParams {
  duration: number;
  resetKey?: string;
  selectionVersion?: number;
  onClose?: () => void;
}

export function useToastAutoDismiss({
  duration,
  resetKey,
  selectionVersion,
  onClose,
}: UseToastAutoDismissParams) {
  const toastRef = useRef<HTMLDivElement | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeStartedAtRef = useRef(0);
  const remainingDurationRef = useRef(duration);
  const pointerInsideRef = useRef(false);
  const textSelectedRef = useRef(false);
  const isClosingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;

    isClosingRef.current = true;
    clearAutoCloseTimer();
    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => onCloseRef.current?.(), FADE_OUT_MS);
  }, [clearAutoCloseTimer]);

  const startAutoCloseTimer = useCallback(
    (delay: number) => {
      clearAutoCloseTimer();
      if (duration <= 0 || delay <= 0 || pointerInsideRef.current || textSelectedRef.current) {
        return;
      }

      closeStartedAtRef.current = Date.now();
      autoCloseTimerRef.current = setTimeout(handleClose, delay);
    },
    [clearAutoCloseTimer, duration, handleClose],
  );

  const pauseAutoCloseTimer = useCallback(() => {
    if (!autoCloseTimerRef.current) return;

    clearAutoCloseTimer();
    remainingDurationRef.current = Math.max(
      0,
      remainingDurationRef.current - (Date.now() - closeStartedAtRef.current),
    );
  }, [clearAutoCloseTimer]);

  const resumeAutoCloseTimer = useCallback(() => {
    if (pointerInsideRef.current || textSelectedRef.current || isClosingRef.current) return;
    startAutoCloseTimer(remainingDurationRef.current);
  }, [startAutoCloseTimer]);

  const hasSelectedToastText = useCallback(() => {
    const selection = window.getSelection();
    const root = toastRef.current;
    if (!selection || selection.isCollapsed || !root) return false;

    for (let index = 0; index < selection.rangeCount; index += 1) {
      const selectedNode = selection.getRangeAt(index).commonAncestorContainer;
      if (root.contains(selectedNode)) return true;
    }

    return false;
  }, []);

  const syncSelectionPauseState = useCallback(() => {
    textSelectedRef.current = hasSelectedToastText();
    if (textSelectedRef.current) {
      pauseAutoCloseTimer();
      return;
    }

    resumeAutoCloseTimer();
  }, [hasSelectedToastText, pauseAutoCloseTimer, resumeAutoCloseTimer]);

  useEffect(() => {
    isClosingRef.current = false;
    setIsVisible(true);
    clearAutoCloseTimer();

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    remainingDurationRef.current = duration;
    startAutoCloseTimer(duration);

    return () => {
      clearAutoCloseTimer();
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [clearAutoCloseTimer, duration, resetKey, startAutoCloseTimer]);

  useEffect(() => {
    syncSelectionPauseState();
  }, [selectionVersion, syncSelectionPauseState]);

  const handleMouseEnter = useCallback(() => {
    pointerInsideRef.current = true;
    pauseAutoCloseTimer();
  }, [pauseAutoCloseTimer]);

  const handleMouseLeave = useCallback(() => {
    pointerInsideRef.current = false;
    syncSelectionPauseState();
  }, [syncSelectionPauseState]);

  return {
    toastRef,
    isVisible,
    handleClose,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseUp: syncSelectionPauseState,
  };
}
